/**
 * settingsV2Store — estado runtime de la vista previa "Settings V2".
 *
 * Mismo patrón que `pmsRuntimeStore.js`: un módulo, `useSyncExternalStore`,
 * cero persistencia. Al recargar la página vuelve el dataset semilla.
 *
 *   - No llama a ningún endpoint (ni lectura ni escritura).
 *   - No toca `project_settings`, ni usuarios, ni integraciones reales.
 *   - No usa localStorage: la demo PMS existente tampoco lo hace y aquí
 *     persistir daría la falsa sensación de haber guardado algo.
 *
 * Cada mutación incrementa `saveTick` para que la interfaz pueda mostrar el
 * aviso discreto "Guardado en la vista previa" sin que ningún componente
 * mantenga su propio temporizador.
 */
import { useSyncExternalStore } from "react";
import {
  PROPERTY, UNIT_TYPES, UNITS, ALLOCATION_POOLS, ALLOCATION_ADVANCED,
  BOOKING_RULES, GUEST_REQUIREMENTS, SERVICES, FINANCE, TEAM,
  WEBSITE, NOTIFICATIONS, SALES_CHANNELS, INTEGRATIONS, UNIT_LABELS,
} from "@/data/settingsV2Demo";

// ---------------------------------------------------------------------------
// Estado
// ---------------------------------------------------------------------------

function seed() {
  return {
    property: { ...PROPERTY },
    unitTypes: UNIT_TYPES.map((t) => ({ ...t })),
    units: UNITS.map((u) => ({ ...u })),
    pools: ALLOCATION_POOLS.map((p) => ({ ...p, unit_ids: [...p.unit_ids] })),
    allocation: { ...ALLOCATION_ADVANCED },
    booking: { ...BOOKING_RULES, owner_stay_blackout: [...BOOKING_RULES.owner_stay_blackout] },
    guests: { ...GUEST_REQUIREMENTS, fields: { ...GUEST_REQUIREMENTS.fields } },
    services: SERVICES.map((s) => ({ ...s, origin: "seed" })),
    finance: { ...FINANCE },
    team: TEAM.map((m) => ({ ...m })),
    channels: SALES_CHANNELS.map((c) => ({ ...c })),
    website: { ...WEBSITE, sections: { ...WEBSITE.sections }, languages: [...WEBSITE.languages] },
    notifications: Object.fromEntries(
      Object.entries(NOTIFICATIONS).map(([k, v]) => [k, { ...v }]),
    ),
    integrations: INTEGRATIONS.map((i) => ({ ...i })),
    saveTick: 0,
  };
}

let state = seed();
const listeners = new Set();

function emit(next) {
  state = next;
  for (const fn of listeners) fn();
}

/** Toda mutación pasa por aquí, así el aviso de guardado es automático. */
function commit(patch) {
  emit({ ...state, ...patch, saveTick: state.saveTick + 1 });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Lecturas
// ---------------------------------------------------------------------------

export function getSettingsV2() {
  return state;
}

/** Snapshot completo y reactivo. */
export function useSettingsV2() {
  return useSyncExternalStore(subscribe, getSettingsV2);
}

/** Solo para tests: vuelve al dataset semilla. */
export function resetSettingsV2() {
  emit(seed());
}

// ---------------------------------------------------------------------------
// Mutaciones (todas locales a la sesión)
// ---------------------------------------------------------------------------

/** Merge superficial sobre una sección plana (`property`, `booking`, …). */
export function updateSection(sectionKey, patch) {
  const current = state[sectionKey];
  if (!current || Array.isArray(current)) return;
  commit({ [sectionKey]: { ...current, ...patch } });
}

/** Activa/desactiva un campo dentro de `guests.fields`. */
export function toggleGuestField(fieldKey) {
  const fields = { ...state.guests.fields, [fieldKey]: !state.guests.fields[fieldKey] };
  commit({ guests: { ...state.guests, fields } });
}

/** Activa/desactiva un canal de aviso para un evento concreto. */
export function toggleNotification(eventKey, channelKey) {
  const row = state.notifications[eventKey] || {};
  const nextRow = { ...row, [channelKey]: !row[channelKey] };
  commit({ notifications: { ...state.notifications, [eventKey]: nextRow } });
}

export function updateUnitType(unitTypeId, patch) {
  commit({
    unitTypes: state.unitTypes.map((t) =>
      t.unit_type_id === unitTypeId ? { ...t, ...patch } : t),
  });
}

export function updateUnit(unitId, patch) {
  commit({
    units: state.units.map((u) => (u.unit_id === unitId ? { ...u, ...patch } : u)),
  });
}

export function updatePool(poolId, patch) {
  commit({
    pools: state.pools.map((p) => (p.pool_id === poolId ? { ...p, ...patch } : p)),
  });
}

export function updateAllocation(patch) {
  commit({ allocation: { ...state.allocation, ...patch } });
}

let sessionServiceCounter = 0;
function nextServiceId() {
  sessionServiceCounter += 1;
  return `SVC-SESSION-${String(sessionServiceCounter).padStart(3, "0")}`;
}

/** Alta o edición de un servicio de la vista previa. Devuelve el service_id. */
export function upsertService(payload) {
  const existing = payload.service_id
    && state.services.some((s) => s.service_id === payload.service_id);

  if (existing) {
    commit({
      services: state.services.map((s) =>
        s.service_id === payload.service_id ? { ...s, ...payload } : s),
    });
    return payload.service_id;
  }

  const service = {
    service_id: nextServiceId(),
    name: "",
    category: "other",
    description: "",
    icon: "Sparkle",
    image_set: false,
    cost_price_eur: 0,
    sale_price_eur: 0,
    tax_included: true,
    pricing_unit: "per_stay",
    supplier: "",
    lead_time_hours: 24,
    availability: "",
    active: true,
    ...payload,
    origin: "created_in_session",
  };
  commit({ services: [...state.services, service] });
  return service.service_id;
}

let sessionMemberCounter = 0;

/**
 * Alta de un miembro del equipo EN LA VISTA PREVIA. No crea usuarios, no
 * envía invitaciones y no toca ningún endpoint: es la maqueta del futuro
 * modelo de equipo por propiedad.
 */
export function addTeamMember(payload) {
  sessionMemberCounter += 1;
  const member = {
    member_id: `TM-SESSION-${String(sessionMemberCounter).padStart(3, "0")}`,
    name: "",
    role: "reception",
    scope: state.property.name,
    status: "invited",
    email: "",
    phone: "",
    ...payload,
    origin: "created_in_session",
  };
  commit({ team: [...state.team, member] });
  return member.member_id;
}

export function updateTeamMember(memberId, patch) {
  commit({
    team: state.team.map((m) => (m.member_id === memberId ? { ...m, ...patch } : m)),
  });
}

export function toggleService(serviceId) {
  commit({
    services: state.services.map((s) =>
      s.service_id === serviceId ? { ...s, active: !s.active } : s),
  });
}

// ---------------------------------------------------------------------------
// Derivados
// ---------------------------------------------------------------------------

/** Singular/plural configurados para "unidad" — el vocabulario del cliente. */
export function unitLabelFor(property) {
  const chosen = UNIT_LABELS.find((l) => l.value === property.unit_label);
  if (property.unit_label === "custom" && property.unit_label_custom) {
    const custom = property.unit_label_custom.trim();
    return { singular: custom, plural: `${custom}s` };
  }
  return chosen
    ? { singular: chosen.singular, plural: chosen.plural }
    : { singular: "Unidad", plural: "Unidades" };
}

/**
 * Progreso de configuración. Se calcula de verdad sobre el estado actual —
 * no es una barra decorativa — pero se presenta sin gamificar: un porcentaje
 * y, como mucho, qué falta.
 *
 * Solo se puntúa lo que se puede cambiar DESDE AQUÍ. Conectar un portal o
 * contratar una pasarela no se puede hacer en la vista previa, y el plazo de
 * conservación de documentos está aplazado a propósito: penalizar cualquiera
 * de esas cosas sería pedirle al usuario algo que no puede hacer.
 */
export function completeness(s = state) {
  const checks = [
    // Propiedad
    ["property", !!s.property.name],
    ["property", !!s.property.type],
    ["property", !!s.property.internal_code],
    ["property", !!s.property.description],
    ["property", !!s.property.country && !!s.property.region && !!s.property.city],
    ["property", !!s.property.address],
    ["property", !!s.property.timezone],
    ["property", !!s.property.currency_operational && !!s.property.currency_reporting],
    ["property", (s.property.languages || []).length > 0],
    ["property", !!s.property.check_in_time && !!s.property.check_out_time],
    ["property", !!s.property.contact_email && !!s.property.contact_phone],
    ["property", !!s.property.logo_set],
    ["property", !!s.property.cover_set],
    // Alojamientos
    ["units", s.unitTypes.length > 0],
    ["units", s.unitTypes.every((t) => t.photos_count > 0)],
    ["units", s.unitTypes.every((t) => t.amenities_count > 0)],
    ["units", s.unitTypes.every((t) => t.base_rate_eur > 0)],
    ["units", s.units.length > 0],
    ["units", s.units.every((u) => u.owner_assigned)],
    // Reglas
    ["booking", !!s.booking.min_stay_nights],
    ["booking", !!s.booking.cancellation_policy],
    ["booking", !!s.booking.hold_duration_hours],
    ["booking", !!s.booking.owner_stay_quota_nights],
    // Huésped
    ["guests", !!s.guests.online_checkin_enabled],
    ["guests", Object.values(s.guests.fields).some(Boolean)],
    ["guests", !!s.guests.consent_house_rules && !!s.guests.consent_privacy],
    // Servicios
    ["services", s.services.length > 0],
    ["services", s.services.filter((x) => x.active).length >= 3],
    ["services", s.services.every((x) => x.image_set)],
    // Finanzas
    ["finance", !!s.finance.tax_pct],
    ["finance", (s.finance.channel_costs || []).length > 0],
    ["finance", !!s.finance.management_fee_pct],
    ["finance", !!s.finance.owner_statement_day],
    // Equipo
    ["team", s.team.length > 0],
    ["team", s.team.every((m) => m.status === "active")],
    // Canales — solo se puntúa lo que ya está en marcha: conectar un portal
    // no se puede hacer desde esta vista previa, así que no puede restar.
    ["channels", s.channels.some((c) => c.status === "connected_demo")],
    // Web
    ["website", !!s.website.logo_set],
    ["website", !!s.website.style && !!s.website.tone],
    ["website", !!s.website.hero_set],
    ["website", !!s.website.seo_title],
    ["website", s.website.domain_status === "configured"],
    ["website", !!s.website.direct_booking_enabled],
    // Notificaciones
    ["notifications", Object.values(s.notifications).every((row) => Object.values(row).some(Boolean))],
    // Integraciones
    ["integrations", s.integrations.filter((i) => i.status === "connected").length >= 3],
  ];

  const total = checks.length;
  const done = checks.filter(([, ok]) => ok).length;
  const pending = {};
  for (const [section, ok] of checks) {
    if (!ok) pending[section] = (pending[section] || 0) + 1;
  }
  return { done, total, pct: Math.round((done / total) * 100), pending };
}
